import { DEFAULT_TIMEZONE, enFirst } from '@yoyoclub/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import PassRefundPolicyModal from '../components/booking/PassRefundPolicyModal';
import { useAuth } from '../hooks/useAuth';
import { dateLocale } from '../i18n/locale';
import { localize } from '../i18n/script';
import { memberRewardsApi } from '../services/memberRewardsApi';
import { passApi, type PassDetail } from '../services/passApi';
import { storeCreditApi } from '../services/storeCreditApi';
import { trackBeginCheckout } from '../utils/analytics';
import { forgetMemberCode, getRememberedMemberCode, promoFailureText } from '../utils/memberCode';

export default function PassCheckout() {
  const { t, i18n } = useTranslation(['pass', 'booking', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { id: passId } = useParams<{ id: string }>();
  const { isAuthenticated, getToken, loading: authLoading } = useAuth();

  const [pass, setPass] = useState<PassDetail | null>(
    (location.state as { pass?: PassDetail } | null)?.pass ?? null
  );
  const [passLoading, setPassLoading] = useState(
    !(location.state as { pass?: PassDetail } | null)?.pass
  );
  const [creditBalance, setCreditBalance] = useState(0);
  const [refundPolicyAgreed, setRefundPolicyAgreed] = useState(false);
  const [showRefundPolicyModal, setShowRefundPolicyModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A member's new user promo code; passes take no platform promo code (member rewards PRD §3.2)
  const [codeInput, setCodeInput] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [appliedInvite, setAppliedInvite] = useState<{
    code: string;
    discountValue: number;
  } | null>(null);
  const inviteTriedRef = useRef<string | null>(null);
  // Accounts that attended a class or already used a new user promo code never
  // see the code box: a pass takes no other code (member rewards PRD §3.2).
  const [newUserEligible, setNewUserEligible] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    memberRewardsApi
      .eligibility()
      .then((eligible) => !cancelled && setNewUserEligible(eligible))
      .catch(() => !cancelled && setNewUserEligible(true));
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname]);

  // Recover pass detail when page is refreshed or opened directly
  useEffect(() => {
    if (pass || !passId) {
      setPassLoading(false);
      return;
    }

    const fetchPass = async () => {
      try {
        setPassLoading(true);
        const data = await passApi.getPassById(passId);
        setPass(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load pass');
      } finally {
        setPassLoading(false);
      }
    };

    fetchPass();
  }, [pass, passId]);

  // An new user promo code from a shared link (member rewards PRD §5.2): applied when
  // it works for this buyer, dropped silently when it does not.
  const loadedPassId = pass?.id;
  useEffect(() => {
    const remembered = getRememberedMemberCode();
    if (!remembered || !loadedPassId || !isAuthenticated || appliedInvite) return;
    if (newUserEligible !== true) return;
    const key = `${remembered}|${loadedPassId}`;
    if (inviteTriedRef.current === key) return;
    inviteTriedRef.current = key;
    void (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const result = await passApi.validateInvitationCode(loadedPassId, remembered, token);
        if (result.valid && result.isMemberCode && result.discountValue != null) {
          setAppliedInvite({ code: remembered, discountValue: result.discountValue });
        } else {
          forgetMemberCode();
        }
      } catch {
        // Silent: the buyer can still type the code.
      }
    })();
  }, [loadedPassId, isAuthenticated, appliedInvite, newUserEligible, getToken]);

  const applyInvitationCode = async () => {
    if (!pass || !codeInput.trim()) return;
    setCodeChecking(true);
    setCodeError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('No access token');
      const code = codeInput.trim().toUpperCase();
      const result = await passApi.validateInvitationCode(pass.id, code, token);
      if (result.valid && result.discountValue != null) {
        setAppliedInvite({ code, discountValue: result.discountValue });
        setCodeInput('');
      } else {
        setCodeError(
          result.reason === 'PASS_PROMO_NOT_ALLOWED'
            ? t('pass:checkout.invitation.platformNotAllowed')
            : promoFailureText(t, result.reason, result.message)
        );
      }
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : 'Failed to check the code');
    } finally {
      setCodeChecking(false);
    }
  };

  // GA4: track begin_checkout for pass (fire once when pass loads)
  useEffect(() => {
    if (!pass) return;
    trackBeginCheckout({
      type: 'pass',
      title: enFirst(pass.titleCN, pass.titleEN),
      value: pass.price / 100,
    });
  }, [pass]);

  // Fetch store credit balance
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchBalance = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const balance = await storeCreditApi.getBalance(token);
        setCreditBalance(balance);
      } catch {
        // Non-critical
      }
    };
    fetchBalance();
  }, [isAuthenticated, getToken]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (passLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!pass) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container-custom max-w-4xl text-center">
          <p className="text-gray-600 mb-4">{t('pass:checkout.noPassData')}</p>
          <button
            onClick={() => navigate('/activities')}
            className="text-primary hover:underline font-medium"
          >
            {t('pass:detail.backToActivities')}
          </button>
        </div>
      </div>
    );
  }

  if (!pass.isBuyable) {
    const isSoldOut = pass.soldCount >= pass.maxSales;
    const isExpired = new Date(pass.expiresAt) <= new Date();
    const reason = isSoldOut
      ? t('pass:detail.soldOut')
      : isExpired
        ? t('pass:detail.expired')
        : t('pass:detail.paused');

    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container-custom max-w-4xl text-center">
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600 mb-4">
            {reason}
          </div>
          <p className="text-gray-600 mb-4">{t('pass:checkout.notBuyable')}</p>
          <button onClick={() => navigate(-1)} className="text-primary hover:underline font-medium">
            {t('pass:checkout.backButton')}
          </button>
        </div>
      </div>
    );
  }

  const passTitle = localize(pass.titleCN, pass.titleEN, i18n.language);
  const totalPriceCents = pass.price;
  const inviteDiscountCents = appliedInvite
    ? Math.min(appliedInvite.discountValue, totalPriceCents)
    : 0;
  const afterInviteCents = totalPriceCents - inviteDiscountCents;
  const creditToUse = Math.min(creditBalance, afterInviteCents);
  const remainingToPay = (afterInviteCents - creditToUse) / 100;
  const isCreditOnly = creditToUse >= afterInviteCents;

  const expiresFormatted = new Date(pass.expiresAt).toLocaleDateString(dateLocale(i18n.language), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: DEFAULT_TIMEZONE,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!refundPolicyAgreed) {
      setError(t('pass:checkout.refundPolicy.required'));
      return;
    }

    try {
      setSubmitting(true);
      const token = await getToken();
      if (!token) throw new Error('No access token');

      const policyAcceptedAt = new Date().toISOString();
      const result = await passApi.purchasePass(
        pass.id,
        policyAcceptedAt,
        token,
        i18n.language,
        appliedInvite?.code
      );
      if (appliedInvite) forgetMemberCode();

      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else if (result.userPassId) {
        navigate(`/pass-purchase-success?userPassId=${result.userPassId}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to purchase pass');
      setSubmitting(false);
    }
  };

  return (
    <>
      {showRefundPolicyModal && (
        <PassRefundPolicyModal onClose={() => setShowRefundPolicyModal(false)} />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container-custom max-w-4xl">
          {/* Back button */}
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="text-primary hover:underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t('pass:checkout.backButton')}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('pass:checkout.title')}
                </h1>

                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Pass Info */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('pass:checkout.passInfo')}
                    </h2>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <p className="font-medium text-gray-900">{passTitle}</p>
                      <p className="text-sm text-gray-600">
                        {t('pass:detail.sessionsCount', { count: pass.totalSessions })}
                      </p>
                      <p className="text-sm text-gray-600">
                        {t('pass:detail.validUntil')} {expiresFormatted}
                      </p>
                    </div>
                  </div>

                  {/* New user promo code (member rewards PRD §3.2) */}
                  {newUserEligible === true && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-3">
                        {t('pass:checkout.invitation.title')}
                      </h2>
                      {appliedInvite ? (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-green-700">
                            {t('pass:checkout.invitation.applied', { code: appliedInvite.code })}
                          </span>
                          <button
                            type="button"
                            onClick={() => setAppliedInvite(null)}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >
                            {t('pass:checkout.invitation.remove')}
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={codeInput}
                              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                              placeholder={t('pass:checkout.invitation.placeholder')}
                              maxLength={20}
                              className="flex-1 min-w-0 border border-gray-200 rounded px-2.5 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-primary"
                            />
                            <button
                              type="button"
                              onClick={applyInvitationCode}
                              disabled={!codeInput.trim() || codeChecking}
                              className="shrink-0 px-3 py-1.5 text-sm text-primary font-medium hover:text-primary/80 disabled:text-gray-300 disabled:cursor-not-allowed"
                            >
                              {codeChecking ? '...' : t('pass:checkout.invitation.apply')}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('pass:checkout.invitation.hint')}
                          </p>
                          {codeError && <p className="text-xs text-red-500 mt-1">{codeError}</p>}
                        </>
                      )}
                    </div>
                  )}

                  {/* Store Credit */}
                  {creditBalance > 0 && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">
                          {t('pass:checkout.storeCredit.balance')}
                        </span>
                        <span className="font-semibold text-gray-900">
                          ${(creditBalance / 100).toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('pass:checkout.storeCredit.applied')}
                      </p>
                    </div>
                  )}

                  {/* Refund Policy Checkbox */}
                  <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={refundPolicyAgreed}
                        onChange={(e) => setRefundPolicyAgreed(e.target.checked)}
                        className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">
                        {t('pass:checkout.refundPolicy.agree')}
                        <button
                          type="button"
                          onClick={() => setShowRefundPolicyModal(true)}
                          className="text-primary hover:underline font-medium"
                        >
                          {t('pass:checkout.refundPolicy.link')}
                        </button>
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting || !refundPolicyAgreed}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                      submitting || !refundPolicyAgreed
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary-dark'
                    }`}
                  >
                    {submitting
                      ? t('pass:checkout.submit.processing')
                      : isCreditOnly
                        ? t('pass:checkout.submit.payWithCredit')
                        : t('pass:checkout.submit.button', { price: remainingToPay.toFixed(2) })}
                  </button>
                </form>
              </div>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-24 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('pass:checkout.summary.title')}
                </h3>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('pass:checkout.summary.passName')}</span>
                    <span className="font-medium text-gray-900 text-right max-w-[60%]">
                      {passTitle}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('pass:checkout.summary.sessions')}</span>
                    <span className="font-medium text-gray-900">{pass.totalSessions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('pass:checkout.summary.validUntil')}</span>
                    <span className="font-medium text-gray-900">{expiresFormatted}</span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('pass:checkout.summary.total')}</span>
                    <span className="font-bold text-gray-900">
                      ${(totalPriceCents / 100).toFixed(2)}
                    </span>
                  </div>
                  {inviteDiscountCents > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>
                        {t('pass:checkout.invitation.applied', { code: appliedInvite?.code ?? '' })}
                      </span>
                      <span>-${(inviteDiscountCents / 100).toFixed(2)}</span>
                    </div>
                  )}
                  {creditToUse > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t('pass:checkout.summary.creditApplied')}</span>
                      <span>-${(creditToUse / 100).toFixed(2)}</span>
                    </div>
                  )}
                  {(creditToUse > 0 || inviteDiscountCents > 0) && (
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">
                        {t('pass:checkout.summary.remaining')}
                      </span>
                      <span className="font-bold text-primary">${remainingToPay.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
